import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  HStack,
  Text,
  FormLabel,
  Input,
  Button,
} from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import { useSchoolClassModal } from '$/pages/SchoolClassesPage/hooks/useSchoolClassModal';
import {
  createSchoolClass,
  deleteSchoolClass,
  updateSchoolClass,
} from '$/services/usecases/school/schoolClasses';

type ModalInfo = {
  header: string;
  body?: ReactNode;
  footer?: ReactNode;
};

export const SchoolClassModal = () => {
  const { t } = useTranslation();
  const isOpen = useSchoolClassModal.useIsOpen();
  const modalMode = useSchoolClassModal.useModalMode();
  const onClose = useSchoolClassModal.useOnClose();
  const schoolClass = useSchoolClassModal.useSchoolClass();
  const user = useAuthenticationStore.useUser();
  const [nameInputValue, setNameInputValue] = useState('');

  useEffect(() => {
    if (schoolClass) {
      setNameInputValue(schoolClass.name);
    } else {
      setNameInputValue('');
    }
  }, [schoolClass, isOpen]);

  const handleClose = () => {
    setNameInputValue('');
    onClose();
  };

  const createClassMutation = useMutation({
    mutationFn: createSchoolClass,
    onSuccess: handleClose,
  });

  const renameClassMutation = useMutation({
    mutationFn: updateSchoolClass,
    onSuccess: handleClose,
  });

  const deleteClassMutation = useMutation({
    mutationFn: deleteSchoolClass,
    onSuccess: handleClose,
  });

  const isRenameSaveDisabled =
    !nameInputValue ||
    nameInputValue === schoolClass?.name ||
    renameClassMutation.isPending;
  const isCreateSaveDisabled = !nameInputValue || createClassMutation.isPending;

  const { header, body, footer }: ModalInfo = (() => {
    switch (modalMode) {
      case 'createClass':
        return {
          header: t('school.schoolClasses.createClassModal.header'),
          body: (
            <>
              <FormLabel fontSize='sm'>
                {t('school.schoolClasses.createClassModal.classNameLabel')}
              </FormLabel>
              <Input
                onChange={(event) => setNameInputValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !isCreateSaveDisabled) {
                    createClassMutation.mutate({
                      name: nameInputValue,
                      schoolId: user!.schoolId!,
                      teacherId: user!.id,
                      studentIds: [],
                    });
                  }
                }}
                value={nameInputValue}
              />
            </>
          ),
          footer: (
            <HStack justify='space-between' w='full'>
              <Button
                fontSize='sm'
                isDisabled={createClassMutation.isPending}
                onClick={handleClose}
                variant='text'
              >
                {t('school.schoolClasses.createClassModal.cancelButton')}
              </Button>
              <Button
                fontSize='sm'
                isDisabled={isCreateSaveDisabled}
                isLoading={createClassMutation.isPending}
                onClick={() =>
                  createClassMutation.mutate({
                    name: nameInputValue,
                    schoolId: user!.schoolId!,
                    teacherId: user!.id,
                    studentIds: [],
                  })
                }
              >
                {t('school.schoolClasses.createClassModal.createButton')}
              </Button>
            </HStack>
          ),
        };
      case 'renameClass':
        return {
          header: t('school.schoolClasses.renameClassModal.header'),
          body: (
            <>
              <FormLabel fontSize='sm'>
                {t('school.schoolClasses.renameClassModal.classNameLabel')}
              </FormLabel>
              <Input
                onChange={(event) => setNameInputValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !isRenameSaveDisabled) {
                    renameClassMutation.mutate({
                      ...schoolClass!,
                      name: nameInputValue,
                    });
                  }
                }}
                value={nameInputValue}
              />
            </>
          ),
          footer: (
            <HStack justify='space-between' w='full'>
              <Button
                fontSize='sm'
                isDisabled={renameClassMutation.isPending}
                onClick={handleClose}
                variant='text'
              >
                {t('school.schoolClasses.renameClassModal.cancelButton')}
              </Button>
              <Button
                fontSize='sm'
                isDisabled={isRenameSaveDisabled}
                isLoading={renameClassMutation.isPending}
                onClick={() =>
                  renameClassMutation.mutate({
                    ...schoolClass!,
                    name: nameInputValue,
                  })
                }
              >
                {t('school.schoolClasses.renameClassModal.saveButton')}
              </Button>
            </HStack>
          ),
        };
      case 'deleteClass':
        return {
          header: t('school.schoolClasses.deleteClassModal.header'),
          body: (
            <Text>
              <Trans
                i18nKey='school.schoolClasses.deleteClassModal.deleteInformation'
                values={{ className: schoolClass?.name }}
                shouldUnescape
              />
            </Text>
          ),
          footer: (
            <HStack justify='space-between' w='full'>
              <Button
                fontSize='sm'
                isDisabled={deleteClassMutation.isPending}
                onClick={handleClose}
                variant='text'
              >
                {t('school.schoolClasses.deleteClassModal.cancelButton')}
              </Button>
              <Button
                fontSize='sm'
                isLoading={deleteClassMutation.isPending}
                onClick={() => deleteClassMutation.mutate(schoolClass!)}
                variant='danger'
              >
                {t('school.schoolClasses.deleteClassModal.deleteButton')}
              </Button>
            </HStack>
          ),
        };
    }
  })();

  return (
    <Modal isCentered isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent
        gap='4'
        w='full'
        maxW='498px'
        p='10'
        bg='background'
        borderRadius='12px'
      >
        <ModalHeader p='0' fontSize='h7'>
          <Text>{header}</Text>
          <ModalCloseButton mt='6' mr='6' />
        </ModalHeader>
        <ModalBody mb='6' p='0' fontSize='sm'>
          {body}
        </ModalBody>
        <ModalFooter p='0'>
          <HStack justifyContent='space-between' w='full'>
            {footer}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
